/**
 * 应用外壳。
 *
 * 负责四件事，页面自己一概不管：
 *   · 内部滚动容器（大标题联动和下拉刷新都以它为准）
 *   · 顶部玻璃栏 + 紧缩标题（滚动到一定位置才出现）+ 左侧冰箱切换器
 *   · 底部悬浮标签栏
 *   · 路由切换时给新页面一次入场
 *
 * 页面切换用 `key={route}` 强制重挂载：状态干净，入场动画也自然。
 *
 * 切换器为什么常驻在顶部左侧：冰箱是这个应用最外层的上下文。
 * 玻璃栏是滚动后才淡入的（它只是层背景），但切换器必须**一直在**，
 * 否则用户滚到中间想换个冰箱，得先滚回顶部。
 * 这也是 iOS 的一贯做法 —— 导航栏左侧那个按钮从来不隐藏。
 */

import { useRef } from 'react';

import { useEnterIn, useLargeTitle, usePullToRefresh } from '../motion';
import { ROUTE_TITLES, useRoute } from '../router';
import { useAppData } from '../state/app-context';
import { HouseholdSwitcher } from './HouseholdSwitcher';
import { GLYPHS, IconGlyph } from './IconGlyph';
import { Onboarding } from './Onboarding';
import { TabBar } from './TabBar';
import { BuyListPage } from '../pages/BuyListPage';
import { CookPage } from '../pages/CookPage';
import { FridgePage } from '../pages/FridgePage';
import { SettingsPage } from '../pages/SettingsPage';

export function AppShell(): JSX.Element {
	const { needsOnboarding, authRequired, buyList, refreshAll, generation } = useAppData();
	const [route, navigate] = useRoute();

	const shellRef = useRef<HTMLDivElement | null>(null);
	const scrollerRef = useRef<HTMLElement | null>(null);

	// 路由变化时重建：大标题元素被换掉了，旧的 ScrollTrigger 必须跟着重来
	useLargeTitle({ scrollerRef, shellRef, deps: [route] });
	usePullToRefresh({ scrollerRef, onRefresh: refreshAll });
	const pageRef = useEnterIn<HTMLDivElement>({ deps: [route] });

	/**
	 * 两道门的顺序和它们在服务端的一致：先口令，后冰箱。
	 * 少了哪个都进不去，而两者要用户做的事完全不同，所以不能合成一屏。
	 * hook 必须在 return 之前调用完（见上面的说明），所以判断放在这里而不是更早。
	 */
	if (authRequired || needsOnboarding) {
		return (
			<div className="h-[100dvh] bg-bg">
				<Onboarding />
			</div>
		);
	}

	const openCount = buyList?.openCount ?? 0;

	return (
		<div ref={shellRef} className="relative flex h-[100dvh] flex-col overflow-hidden bg-bg">
			{/* 顶部玻璃栏：默认完全透明，滚动后由 useLargeTitle 淡入。
			    切换器在玻璃之上，且自己把指针事件收回来（这一层是 none）。 */}
			<div className="pointer-events-none absolute inset-x-0 top-0 z-30">
				<div
					data-topbar-glass
					className="glass absolute inset-0 border-b border-separator"
					style={{ opacity: 0 }}
				/>
				<div
					className="relative grid h-12 grid-cols-[1fr_auto_1fr] items-center gap-2 px-3"
					style={{ marginTop: 'env(safe-area-inset-top)' }}
				>
					<div className="flex min-w-0 justify-start">
						<HouseholdSwitcher />
					</div>
					<span
						data-topbar-title
						className="min-w-0 truncate text-[15px] font-semibold text-label"
						style={{ opacity: 0 }}
					>
						{ROUTE_TITLES[route]}
					</span>
					<div aria-hidden />
				</div>
			</div>

			<main
				ref={scrollerRef}
				className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
			>
				{/* 下拉刷新指示器：零高度，靠位移进入视野 */}
				<div data-pull-indicator className="flex h-0 items-center justify-center opacity-0">
					<IconGlyph glyph={GLYPHS.refresh} size={20} className="text-label-2" />
				</div>

				<div
					ref={pageRef}
					style={{ paddingTop: 'calc(3rem + env(safe-area-inset-top))' }}
				>
					<div key={route}>
						{route === 'fridge' && <FridgePage />}
						{route === 'cook' && <CookPage />}
						{route === 'buy' && <BuyListPage />}
						{route === 'settings' && <SettingsPage />}
					</div>
				</div>

				{/* 给底部悬浮标签栏留出空间，否则最后一行会被压住 */}
				<div className="h-[calc(5.5rem+env(safe-area-inset-bottom))]" />
			</main>

			{/* 生成中时在「做菜」那一栏留个点：生成是可以离开页面等的，
			 * 切走之后总得有个地方告诉他还在跑 */}
			<TabBar
				current={route}
				onChange={navigate}
				badges={{ buy: openCount }}
				working={generation.busy ? 'cook' : null}
			/>
		</div>
	);
}
